// src/utils/adminTracking.ts
import { addAdminRegister } from "../api/historyAdminRegisters";

const LS_SESSION_KEY = "admin_session_id";
const MIN_INTERVAL_MS = 1000;

function getSessionId() {
    let sid = localStorage.getItem(LS_SESSION_KEY);
    if (!sid) {
        sid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem(LS_SESSION_KEY, sid);
    }
    return sid;
}

type CtxLike = {
    clients: Client[];
    user?: { email?: string; uid?: string } | null;
};

// 🔁 zwracamy całego klienta (albo null)
function getCurrentClient(ctx: CtxLike): Client | null {
    const uid = ctx.user?.uid;
    if (uid) {
        const byAuth = ctx.clients.find((c) => c.authId === uid);
        if (byAuth) return byAuth;
    }
    const email = ctx.user?.email;
    if (email) {
        const byEmail = ctx.clients.find((c) => c.adminEmail === email);
        if (byEmail) return byEmail;
    }
    return null;
}

// --- throttling na poziomie akcji
const lastActionAt: Record<string, number> = {};
function canLog(key: string) {
    const now = Date.now();
    const last = lastActionAt[key] ?? 0;
    if (now - last < MIN_INTERVAL_MS) return false;
    lastActionAt[key] = now;
    return true;
}

// --- public API

export async function trackEvent(
    ctx: CtxLike,
    action: AdminAction,
    payload?: { screen?: AdminScreen; path?: string; meta?: Record<string, any> }
) {
    // 🚫 TYLKO zalogowani + dopasowany klient
    if (!ctx?.user) return;
    const client = getCurrentClient(ctx);
    if (!client) return;

    const adminId = client.id;
    const sessionId = getSessionId();
    const throttleKey = `${adminId}:${action}:${payload?.screen || ""}`;
    if (!canLog(throttleKey)) return;

    await addAdminRegister({
        adminId,
        date: Date.now(),
        action,
        screen: payload?.screen,
        path: payload?.path,
        sessionId,
        userAgent: navigator.userAgent,
        meta: {
            ...payload?.meta,
            userId: ctx.user.uid,         // 👈 przy okazji doklejamy kto
            userEmail: ctx.user.email,
        },
    });
}

export async function trackEventWithoutThrottle(
    ctx: CtxLike,
    action: AdminAction,
    payload?: { screen?: AdminScreen; path?: string; meta?: Record<string, any> }
) {
    if (!ctx?.user) return;
    const client = getCurrentClient(ctx);
    if (!client) return;

    try {
        await addAdminRegister({
            adminId: client.id,
            date: Date.now(),
            action,
            screen: payload?.screen,
            path: payload?.path,
            sessionId: getSessionId(),
            userAgent: navigator.userAgent,
            meta: {
                ...payload?.meta,
                userId: ctx.user.uid,
                userEmail: ctx.user.email,
            },
        });
    } catch (error) {
        console.error("admin tracking error:", error);
    }
}

// Pomiar czasu spędzonego na ekranie.
// Bug B fix: zapisujemy również `meta` z mount, aby unmount mógł je zachować.
type ScreenTimer = { start: number; meta?: Record<string, any> };
const screenTimers: Record<string, ScreenTimer> = {};

export function trackScreenViewStart(
    ctx: CtxLike,
    screen: AdminScreen,
    path: string,
    meta?: Record<string, any>
) {
    // 🚫 tylko zalogowani + dopasowany klient
    if (!ctx?.user || !getCurrentClient(ctx)) return;

    const key = `${screen}:${path}`;
    screenTimers[key] = { start: performance.now(), meta };
    return trackEvent(ctx, "screen_view", { screen, path, meta });
}

export async function trackScreenViewEnd(
    ctx: CtxLike,
    screen: AdminScreen,
    path: string
) {
    // 🚫 tylko zalogowani + dopasowany klient
    if (!ctx?.user) return;
    const client = getCurrentClient(ctx);
    if (!client) return;

    const key = `${screen}:${path}`;
    const timer = screenTimers[key];
    if (timer != null) {
        const durationMs = Math.round(performance.now() - timer.start);
        delete screenTimers[key];

        const sessionId = getSessionId();
        await addAdminRegister({
            adminId: client.id,
            date: Date.now(),
            action: "screen_view",
            screen,
            path,
            sessionId,
            userAgent: navigator.userAgent,
            durationMs,
            meta: {
                // Bug B fix: zachowujemy `meta` z mount (np. bookId),
                // potem nadpisujemy kontekstem unmount.
                ...timer.meta,
                phase: "unmount",
                userId: ctx.user.uid,
                userEmail: ctx.user.email,
            },
        });
    }
}
